import { useState, useMemo } from "react";
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Button,
  Select,
  Box,
  Container,
  Paper,
  Input,
  SegmentedControl,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import { Link } from "react-router-dom";
import { IconSearch, IconFilter } from "@tabler/icons-react";

// Import JSON data and utilities
import projectsData from "../data/projects.json";
import { filterProjects } from "../utils/projectFilters";

// Import homepage styles for hover effects
import "../styles/homepage.css";

interface Project {
  id: string;
  title: string;
  slug: string;
  description: string;
  status: string;
  startDate: string;
  endDate?: string | null;
  repositoryUrl?: string;
  liveUrl?: string;
  technologyGroups?: Array<{
    category: string;
    technologies: Array<{ name: string; description: string }>;
  }>;
}

interface ProjectCardProps {
  p: Project;
  dense: boolean;
}

function ProjectCard({ p: project, dense }: ProjectCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "COMPLETED":
        return "blue";
      case "ON_HOLD":
        return "yellow";
      case "ARCHIVED":
        return "gray";
      default:
        return "gray";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "Active";
      case "COMPLETED":
        return "Completed";
      case "ON_HOLD":
        return "On Hold";
      case "ARCHIVED":
        return "Archived";
      default:
        return status;
    }
  };

  const allTechs =
    project.technologyGroups?.flatMap((group) =>
      group.technologies.map((tech) => ({ ...tech, category: group.category }))
    ) || [];

  const displayTechs = allTechs.slice(0, dense ? 2 : 3);
  const totalCount = allTechs.length;

  return (
    <Card
      h="100%"
      withBorder
      className="hover-lift"
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--card-background)",
        border: "1px solid rgba(139, 66, 255, 0.15)",
        borderRadius: "12px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      styles={{
        root: {
          "&:hover": {
            border: "1px solid rgba(139, 66, 255, 0.25)",
            transform: "translateY(-6px)",
            boxShadow:
              "0 20px 40px rgba(139, 66, 255, 0.15), 0 10px 20px rgba(0, 0, 0, 0.3)",
          },
        },
      }}
    >
      <Card.Section
        p={dense ? "sm" : "md"}
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack gap={dense ? "xs" : "sm"} style={{ flex: 1 }}>
          <Group justify="space-between" align="flex-start">
            <Title
              order={dense ? 5 : 4}
              lineClamp={2}
              size={dense ? "sm" : "md"}
              style={{ color: "#ffffff" }}
            >
              {project.title}
            </Title>
            <Badge
              size={dense ? "xs" : "sm"}
              variant="light"
              color={getStatusColor(project.status)}
            >
              {getStatusLabel(project.status)}
            </Badge>
          </Group>

          <Text
            size={dense ? "xs" : "sm"}
            lineClamp={dense ? 1 : 2}
            style={{ flex: 1, color: "#d1d5db" }}
          >
            {project.description}
          </Text>

          {allTechs.length > 0 && (
            <Group gap="4px" style={{ flexWrap: "wrap" }}>
              {displayTechs.map((tech, techIndex) => (
                <Badge
                  key={tech.name}
                  size="xs"
                  variant="light"
                  style={{
                    background: `rgba(139, 66, 255, ${0.1 + techIndex * 0.05})`,
                    color: "var(--purple-300)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                  }}
                >
                  {tech.name}
                </Badge>
              ))}
              {totalCount > (dense ? 2 : 3) && (
                <Badge
                  size="xs"
                  variant="light"
                  style={{
                    background: "rgba(139, 66, 255, 0.1)",
                    color: "var(--purple-300)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                  }}
                >
                  +{totalCount - (dense ? 2 : 3)}
                </Badge>
              )}
            </Group>
          )}

          <Group justify="space-between" mt="auto" pt={dense ? "4px" : "xs"}>
            <Text size="xs" fw={500} style={{ color: "#a1a1aa" }}>
              {project.startDate &&
                new Date(project.startDate + "T00:00:00").getFullYear()}
              {project.endDate &&
                ` - ${new Date(project.endDate + "T00:00:00").getFullYear()}`}
            </Text>

            <Group gap="4px">
              {project.repositoryUrl && (
                <Button
                  size="xs"
                  variant="outline"
                  component="a"
                  href={project.repositoryUrl}
                  target="_blank"
                >
                  Code
                </Button>
              )}
              {project.liveUrl && (
                <Button
                  size="xs"
                  variant="outline"
                  component="a"
                  href={project.liveUrl}
                  target="_blank"
                >
                  Live
                </Button>
              )}
              <Button
                size="xs"
                component={Link}
                to={`/projects/${project.slug}`}
                variant="gradient"
                gradient={{
                  from: "var(--purple-400)",
                  to: "var(--purple-600)",
                }}
              >
                Details
              </Button>
            </Group>
          </Group>
        </Stack>
      </Card.Section>
    </Card>
  );
}

function EmptyState() {
  return (
    <Paper
      withBorder
      p="xl"
      ta="center"
      style={{
        backgroundColor: "var(--card-background)",
        border: "1px solid rgba(139, 66, 255, 0.15)",
      }}
    >
      <Text size="lg" fw={500} mb="xs" style={{ color: "#ffffff" }}>
        No projects found
      </Text>
      <Text c="dimmed">
        Try adjusting your search terms or filters to find what you&apos;re
        looking for.
      </Text>
    </Paper>
  );
}

export function ProjectsPage() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string | null>("all");
  const [sort, setSort] = useState<{
    key: "year" | "title";
    dir: "asc" | "desc";
  }>({
    key: "year",
    dir: "desc",
  });
  const [density, setDensity] = useState<"comfortable" | "compact">(
    "comfortable"
  );

  // Filter and search projects
  const filtered = useMemo(() => {
    let projects = filterProjects(
      projectsData.projects,
      query,
      status === "all" ? null : status
    );

    // Sort projects
    projects = projects.sort((a, b) => {
      if (sort.key === "year") {
        const yearA = new Date(a.startDate + "T00:00:00").getFullYear();
        const yearB = new Date(b.startDate + "T00:00:00").getFullYear();
        return sort.dir === "desc" ? yearB - yearA : yearA - yearB;
      } else {
        const titleA = a.title.toLowerCase();
        const titleB = b.title.toLowerCase();
        if (sort.dir === "desc") {
          return titleB.localeCompare(titleA);
        } else {
          return titleA.localeCompare(titleB);
        }
      }
    });

    return projects;
  }, [query, status, sort]);

  // Group by year for subtle sectioning
  const grouped = useMemo(() => {
    return filtered.reduce<Record<number, Project[]>>((acc, p) => {
      const year = new Date(p.startDate + "T00:00:00").getFullYear();
      (acc[year] ||= []).push(p);
      return acc;
    }, {});
  }, [filtered]);

  const dense = density === "compact";

  return (
    <Box
      style={{
        background: "var(--deep-background)",
        minHeight: "100vh",
        paddingBottom: 64,
      }}
    >
      <Container size="lg" pt="xl">
        {/* Page header */}
        <Stack gap={4} mb="sm">
          <Title order={1}>Projects</Title>
          <Text c="dimmed">
            A curated set of builds—from quick experiments to production apps.
          </Text>
        </Stack>

        {/* Controls bar */}
        <Paper
          withBorder
          radius="lg"
          p="md"
          shadow="sm"
          style={{
            background: "var(--card-background)",
            border: "1px solid rgba(139, 66, 255, 0.15)",
          }}
        >
          <Group justify="space-between" align="center" wrap="wrap">
            <Input
              leftSection={<IconSearch size={16} />}
              placeholder="Search projects..."
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              w={{ base: "100%", sm: 420 }}
              style={{
                input: {
                  backgroundColor: "var(--deep-background)",
                  border: "1px solid rgba(139, 66, 255, 0.2)",
                  color: "#ffffff",
                  "&:focus": {
                    borderColor: "rgba(139, 66, 255, 0.4)",
                  },
                },
              }}
            />

            <Group gap="xs" wrap="wrap">
              <Select
                data={[
                  { value: "all", label: "All statuses" },
                  { value: "ACTIVE", label: "Active" },
                  { value: "ON_HOLD", label: "Paused" },
                  { value: "ARCHIVED", label: "Archived" },
                  { value: "COMPLETED", label: "Completed" },
                ]}
                value={status}
                onChange={setStatus}
                leftSection={<IconFilter size={16} />}
                w={180}
                styles={{
                  input: {
                    backgroundColor: "var(--deep-background)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                    color: "#ffffff",
                  },
                }}
              />

              <SegmentedControl
                value={`${sort.key}-${sort.dir}`}
                onChange={(v) => {
                  const [k, d] = v.split("-") as [
                    "year" | "title",
                    "asc" | "desc",
                  ];
                  setSort({ key: k, dir: d });
                }}
                data={[
                  { label: "Year ↓", value: "year-desc" },
                  { label: "Year ↑", value: "year-asc" },
                  { label: "Title A–Z", value: "title-asc" },
                  { label: "Title Z–A", value: "title-desc" },
                ]}
                styles={{
                  root: {
                    backgroundColor: "var(--deep-background)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                  },
                  control: {
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#ffffff",
                  },
                }}
              />

              <SegmentedControl
                value={density}
                onChange={(v) => setDensity(v as any)}
                data={[
                  { label: "Comfortable", value: "comfortable" },
                  { label: "Compact", value: "compact" },
                ]}
                styles={{
                  root: {
                    backgroundColor: "var(--deep-background)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                  },
                  control: {
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#ffffff",
                  },
                }}
              />
            </Group>
          </Group>
        </Paper>

        <Divider my="lg" color="rgba(139, 66, 255, 0.2)" />

        {/* Grouped grid by year */}
        <Stack>
          {Object.entries(grouped)
            .sort(([a], [b]) =>
              sort.dir === "desc"
                ? Number(b) - Number(a)
                : Number(a) - Number(b)
            )
            .map(([yr, items]) => (
              <Stack key={yr}>
                <Group align="center" gap="sm">
                  <Title order={2} style={{ color: "#ffffff" }}>
                    {yr}
                  </Title>
                  <Divider
                    style={{ flex: 1 }}
                    color="rgba(139, 66, 255, 0.2)"
                  />
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  {items.map((p) => (
                    <ProjectCard key={p.id} p={p} dense={dense} />
                  ))}
                </SimpleGrid>
              </Stack>
            ))}
        </Stack>

        {filtered.length === 0 && (
          <Box mt="lg">
            <EmptyState />
          </Box>
        )}
      </Container>
    </Box>
  );
}
